import React, { useState, useEffect } from 'react';
import axios from 'axios';
import LiveBattle from './LiveBattle';
import { API_URL } from '../config';

function Tournament({ user, token }) {
  const [tournaments, setTournaments] = useState([]);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [standings, setStandings] = useState([]);
  const [matches, setMatches] = useState([]);
  const [userMatches, setUserMatches] = useState([]);
  const [userCards, setUserCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('tournaments');
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [showBattle, setShowBattle] = useState(false);

  useEffect(() => {
    fetchTournaments();
    fetchUserCards();
  }, []);

  useEffect(() => {
    if (selectedTournament) {
      fetchTournamentData(selectedTournament.id);
    }
  }, [selectedTournament]);

  const fetchTournaments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/tournaments`);
      setTournaments(response.data);
      
      // Auto-select first active tournament
      const activeTournament = response.data.find(t => t.status === 'active');
      if (activeTournament) {
        setSelectedTournament(activeTournament);
      } else if (response.data.length > 0) {
        setSelectedTournament(response.data[0]);
      }
    } catch (error) {
      setError('Errore nel caricamento dei tornei');
      console.error('Error fetching tournaments:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserCards = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/user-cards`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserCards(response.data);
    } catch (error) {
      console.error('Error fetching user cards:', error);
    }
  };

  const fetchTournamentData = async (tournamentId) => {
    try {
      const [standingsRes, matchesRes, userMatchesRes] = await Promise.all([
        axios.get(`${API_URL}/api/tournaments/${tournamentId}/standings`),
        axios.get(`${API_URL}/api/tournaments/${tournamentId}/matches`),
        axios.get(`${API_URL}/api/tournaments/${tournamentId}/my-matches`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setStandings(standingsRes.data);
      setMatches(matchesRes.data);
      setUserMatches(userMatchesRes.data);
    } catch (error) {
      console.error('Error fetching tournament data:', error);
    }
  };

  const joinTournament = async (tournamentId) => {
    try {
      await axios.post(`${API_URL}/api/tournaments/${tournamentId}/join`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert('Iscrizione completata con successo!');
      fetchTournaments();
    } catch (error) {
      alert(error.response?.data?.error || 'Errore nell\'iscrizione');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('it-IT', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRankIcon = (position) => {
    switch(position) {
      case 1: return '🥇';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return `#${position}`;
    }
  };

  const goToBattle = (match) => {
    setSelectedMatch(match);
    setShowBattle(true);
  };

  const onBattleComplete = () => {
    setShowBattle(false);
    setSelectedMatch(null);
    // Refresh tournament data
    if (selectedTournament) {
      fetchTournamentData(selectedTournament.id);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'scheduled': { text: 'Programmato', class: 'badge-scheduled' },
      'active': { text: 'Attivo', class: 'badge-active' },
      'ended': { text: 'Terminato', class: 'badge-ended' }
    };
    
    const badge = badges[status] || { text: status, class: 'badge-scheduled' };
    return <span className={`status-badge ${badge.class}`}>{badge.text}</span>;
  };

  if (loading) {
    return (
      <div className="container">
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <h2>Caricamento tornei...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="tournament-container">
      <div className="tournament-header">
        <h2>🏟️ Fantavaro Torneo</h2>
        <p>Vince il più gay. Maggiori informazioni prossimamente. Intanto accaparrati le carte più forti e rare</p>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Tournament Selector */}
      <div className="tournament-selector">
        <label><strong>Seleziona Torneo:</strong></label>
        <select 
          value={selectedTournament?.id || ''}
          onChange={(e) => {
            const tournament = tournaments.find(t => t.id === parseInt(e.target.value));
            setSelectedTournament(tournament);
          }}
        >
          <option value="">-- Seleziona un torneo --</option>
          {tournaments.map(tournament => (
            <option key={tournament.id} value={tournament.id}>
              {tournament.name} ({tournament.status})
            </option>
          ))}
        </select>
      </div>

      {selectedTournament && (
        <>
          <div className="tournament-info">
            <h3>{selectedTournament.name}</h3>
            <p>{selectedTournament.description}</p>
            <div className="tournament-stats">
              <span>Status: {getStatusBadge(selectedTournament.status)}</span>
              <span>Partecipanti: {selectedTournament.participant_count || 0}</span>
              <span>Inizio: {formatDate(selectedTournament.start_date)}</span>
            </div>
            
            {selectedTournament.status === 'scheduled' && (
              <button 
                className="btn btn-primary"
                onClick={() => joinTournament(selectedTournament.id)}
                disabled={standings.some(s => s.user_id === user.id)}
              >
                {standings.some(s => s.user_id === user.id) ? 'Già iscritto' : 'Iscriviti'}
              </button>
            )}
          </div>

          <div className="tournament-tabs">
            <button 
              className={`tab-btn ${activeTab === 'standings' ? 'active' : ''}`}
              onClick={() => setActiveTab('standings')}
            >
              🏆 Classifica
            </button>
            <button 
              className={`tab-btn ${activeTab === 'matches' ? 'active' : ''}`}
              onClick={() => setActiveTab('matches')}
            >
              ⚔️ Le Tue Partite
            </button>
            <button 
              className={`tab-btn ${activeTab === 'all-matches' ? 'active' : ''}`}
              onClick={() => setActiveTab('all-matches')}
            >
              📅 Tutti i Match
            </button>
            <button 
              className={`tab-btn ${activeTab === 'cards' ? 'active' : ''}`}
              onClick={() => setActiveTab('cards')}
            >
              🃏 Le Tue Carte
            </button>
          </div>

          {/* Standings Tab */}
          {activeTab === 'standings' && (
            <div className="standings-section">
              <h3>🏆 Classifica Torneo</h3>
              {standings.length === 0 ? (
                <p>Nessun dato di classifica disponibile</p>
              ) : (
                <div className="table-container">
                  <table className="leaderboard-table">
                    <thead>
                      <tr>
                        <th>Pos.</th>
                        <th>Giocatore</th>
                        <th>Punti</th>
                        <th>V</th>
                        <th>P</th>
                        <th>S</th>
                        <th>Diff</th>
                      </tr>
                    </thead>
                    <tbody>
                      {standings.map((player, index) => (
                        <tr 
                          key={player.user_id}
                          className={player.user_id === user.id ? 'current-user' : ''}
                        >
                          <td className="rank-cell">
                            <span className={`rank-badge ${
                              index === 0 ? 'rank-gold' : 
                              index === 1 ? 'rank-silver' : 
                              index === 2 ? 'rank-bronze' : ''
                            }`}>
                              {getRankIcon(index + 1)}
                            </span>
                          </td>
                          <td className="player-cell">
                            {player.username}
                            {player.user_id === user.id && <span className="you-badge">Tu</span>}
                          </td>
                          <td><strong>{player.points}</strong></td>
                          <td>{player.wins}</td>
                          <td>{player.losses}</td>
                          <td>{player.draws}</td>
                          <td>{player.wins - player.losses}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* User Matches Tab */}
          {activeTab === 'matches' && (
            <div className="matches-section">
              <h3>⚔️ Le Tue Prossime Partite</h3>
              {userMatches.length === 0 ? (
                <p>Nessuna partita programmata</p>
              ) : (
                <div className="matches-list">
                  {userMatches.map(match => (
                    <div key={match.id} className="match-card">
                      <div className="match-info">
                        <h4>vs {match.opponent_name}</h4>
                        <p>📅 {formatDate(match.scheduled_time)}</p>
                        <p>Status: {getStatusBadge(match.status)}</p>
                      </div>
                      {(match.status === 'scheduled' || match.status === 'active') && (
                        <button 
                          className="btn btn-primary"
                          onClick={() => goToBattle(match)}
                        >
                          {match.status === 'active' ? '🔴 Live' : '📺 Guarda'}
                        </button>
                      )}
                      {match.status === 'ended' && (
                        <button 
                          className="btn btn-secondary"
                          onClick={() => goToBattle(match)}
                        >
                          📋 Replay
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* All Matches Tab */}
          {activeTab === 'all-matches' && (
            <div className="all-matches-section">
              <h3>📅 Tutti i Match del Torneo</h3>
              {matches.length === 0 ? (
                <p>Nessuna partita disponibile</p>
              ) : (
                <div className="table-container">
                  <table className="matches-table">
                    <thead>
                      <tr>
                        <th>Data</th>
                        <th>Giocatore 1</th>
                        <th>Giocatore 2</th>
                        <th>Risultato</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {matches.map(match => (
                        <tr key={match.id}>
                          <td>{formatDate(match.scheduled_time)}</td>
                          <td>{match.player1_name}</td>
                          <td>{match.player2_name}</td>
                          <td>
                            {match.status === 'ended' ? (
                              <>
                                {match.player1_score} - {match.player2_score}
                                {match.winner_name && (
                                  <div className="winner">🏆 {match.winner_name}</div>
                                )}
                              </>
                            ) : '-'}
                          </td>
                          <td>{getStatusBadge(match.status)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* User Cards Tab */}
          {activeTab === 'cards' && (
            <div className="user-cards-section">
              <h3>🃏 Le Tue Carte ({userCards.length})</h3>
              {userCards.length === 0 ? (
                <div className="no-cards">
                  <p>Non hai ancora carte! Partecipa alle aste per ottenerne.</p>
                </div>
              ) : (
                <div className="user-cards-grid">
                  {userCards.map(card => {
                    const metadata = card.metadata ? 
                      (typeof card.metadata === 'string' ? JSON.parse(card.metadata) : card.metadata) : {};
                    
                    return (
                      <div key={card.id} className="user-card-item">
                        {card.image_url && (
                          <img 
                            src={`${API_URL}/cards/${card.image_url}`}
                            alt={card.name}
                            className="user-card-image"
                          />
                        )}
                        <h4>{card.name}</h4>
                        {metadata.rarity && (
                          <div className="card-rarity">{metadata.rarity}</div>
                        )}
                        <div className="purchase-price">
                          💰 {card.purchase_price?.toLocaleString()} patapizze
                        </div>
                        {metadata.atk && metadata.def && (
                          <div className="card-stats">
                            ATK: {metadata.atk} / DEF: {metadata.def}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {tournaments.length === 0 && (
        <div className="no-tournaments">
          <h3>🏟️ Nessun torneo disponibile</h3>
          <p>A pezzo demmerda, i tornei andranno creati dagli amministratori dopo la conclusione delle aste, mica adè</p>
        </div>
      )}

      {/* Live Battle */}
      {showBattle && selectedMatch && (
        <LiveBattle 
          matchId={selectedMatch.id}
          user={user}
          token={token}
          onClose={onBattleComplete}
        />
      )}
    </div>
  );
}

export default Tournament;